import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Role } from '@connect-x-app/shared-domain-models';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Spinner } from '@connect-x-app/web-ui';
import { CellContext, createColumnHelper, getCoreRowModel, HeaderContext, useReactTable } from '@tanstack/react-table';

import { EmptyState, TableCell, TableHeader, TableLayout } from '../../shared';
import { useGetRoles } from './queries';

import './roles.scss';

const columnHelper = createColumnHelper<Role>();

export const Roles: FC = () => {
  const { t } = useTranslation();
  const { data: roles, isLoading } = useGetRoles();
  const navigate = useNavigate();

  const renderCell = useCallback(({ ...props }: CellContext<Role, string>) => {
    return <TableCell {...props} />;
  }, []);

  const renderHeader = useCallback(({ title, ...props }: HeaderContext<Role, string> & { title: string }) => {
    return <TableHeader {...props} title={title} />;
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('airlineRole', {
        cell: props => renderCell(props),
        header: props => renderHeader({ ...props, title: t<string>('ROLE_TAGS_COLUMN_NAME') }),
        id: 'airlineRole',
      }),
      columnHelper.accessor('displayName', {
        cell: props => renderCell(props),
        header: props => renderHeader({ ...props, title: t<string>('ROLE_DISPLAY_NAME_COLUMN_NAME') }),
        id: 'displayName',
      }),
    ],
    [renderCell, renderHeader, t],
  );

  // Provide a default empty array if roles is undefined and filter out any undefined airlineRoles
  const filteredAndSortedRoles = (Array.isArray(roles) ? roles : []).sort((a, b) => a.airlineRole.localeCompare(b.airlineRole));

  const table = useReactTable({
    columns,
    data: filteredAndSortedRoles,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderEmptyState = () => (
    <EmptyState description={t('ROLES_EMPTY_STATE_DESCRIPTION')} title={t('ROLES_EMPTY_STATE_TITLE')} />
  );

  if (isLoading || !roles) {
    return <Spinner>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  return (
    <main className="roles-container" data-test={ADMIN_TEST_IDS.roles.screen}>
      <TableLayout
        emptyState={renderEmptyState}
        onRowClicked={row => navigate(`${row.original.id}/`)}
        rowClickAllowed
        table={table}
      />
    </main>
  );
};
